import { toCountInput, toSlideCount } from "@/components/lessonGenerator/countRules";
import type {
  DataSource,
  ReferenceSlide,
  SlideCount,
  SubslideParent,
} from "@/components/lessonGenerator/types";
import { useFragment } from "@/generated";
import {
  CreateSectionPresetDocument,
  DeleteSectionPresetDocument,
  GetSlidesByIdsDocument,
  type LessonDataSourceEnum,
  SectionPresetFieldsFragmentDoc,
  type SectionPresetInput,
  SectionPresetsDocument,
  SlideFieldsFragmentDoc,
  UpdateSectionPresetDocument,
} from "@/generated/graphql";
import type { SlideFields } from "@/types/Slide";
import type { SlideTemplate } from "@ai-teach/types";
import { useMutation, useQuery } from "@apollo/client/react";

export interface SavedSection {
  id: string;
  name: string;
  template: SlideTemplate;
  referenceSlide: ReferenceSlide;
  context: DataSource[];
  sectionScopes?: string[];
  subLists?: string[];
  count?: SlideCount;
  parent?: SubslideParent;
  prompt: string;
  randomizeChoicePositions: boolean;
  generateSentence: boolean;
}

export interface SavedSectionFields {
  template: SlideTemplate;
  referenceSlideId: string;
  context: DataSource[];
  sectionScopes?: string[];
  subLists?: string[];
  count?: SlideCount;
  parent?: SubslideParent;
  prompt: string;
  randomizeChoicePositions: boolean;
  generateSentence: boolean;
}

/** The half of a section a template owns, as both the preset panels and the editor hold it. */
export function toSavedFields(section: {
  template: SlideTemplate;
  referenceSlide: { id: string };
  context: DataSource[];
  sectionScopes?: string[];
  subLists?: string[];
  count?: SlideCount;
  parent?: SubslideParent;
  prompt: string;
  randomizeChoicePositions?: boolean;
  generateSentence?: boolean;
}): SavedSectionFields {
  return {
    template: section.template,
    referenceSlideId: section.referenceSlide.id,
    context: section.context,
    ...(section.sectionScopes?.length ? { sectionScopes: section.sectionScopes } : {}),
    ...(section.subLists?.length ? { subLists: section.subLists } : {}),
    ...(section.count ? { count: section.count } : {}),
    ...(section.parent ? { parent: section.parent } : {}),
    prompt: section.prompt,
    randomizeChoicePositions: section.randomizeChoicePositions ?? false,
    generateSentence: section.generateSentence ?? false,
  };
}

export function toSectionPresetInput(name: string, fields: SavedSectionFields): SectionPresetInput {
  return {
    name,
    template: fields.template,
    referenceSlideId: fields.referenceSlideId,
    context: fields.context as LessonDataSourceEnum[],
    sectionScopes: fields.sectionScopes ?? [],
    subLists: fields.subLists ?? [],
    count: fields.count ? toCountInput(fields.count) : null,
    parent: fields.parent ?? null,
    prompt: fields.prompt,
    randomizeChoicePositions: fields.randomizeChoicePositions,
    generateSentence: fields.generateSentence,
  };
}

/** Saved sections whose reference slide has since been deleted are left out — nothing can apply them. */
export function useSectionPresets() {
  const { data, loading, refetch } = useQuery(SectionPresetsDocument, {
    // A null template lists every saved section, whatever template it was saved for.
    variables: { template: null },
    fetchPolicy: "cache-and-network",
  });

  const stored = (data?.sectionPresets ?? []).map((one) =>
    useFragment(SectionPresetFieldsFragmentDoc, one)
  );
  const referenceSlideIds = [...new Set(stored.map((one) => one.referenceSlideId))];

  const { data: slidesData, loading: slidesLoading } = useQuery(GetSlidesByIdsDocument, {
    variables: { ids: referenceSlideIds },
    skip: referenceSlideIds.length === 0,
    fetchPolicy: "cache-first",
  });

  const slidesById = new Map(
    (slidesData?.getSlidesByIds ?? [])
      .map((slide) => useFragment(SlideFieldsFragmentDoc, slide))
      .map((slide) => [slide.id, slide as SlideFields])
  );

  const sections: SavedSection[] = stored.flatMap((preset) => {
    const slide = slidesById.get(preset.referenceSlideId);
    if (!slide) return [];
    return [
      {
        id: preset.id,
        name: preset.name,
        template: preset.template as SlideTemplate,
        referenceSlide: {
          id: slide.id,
          name: slide.name,
          template: slide.template,
          tagIds: slide.tagIds,
          slide,
        },
        context: preset.context as DataSource[],
        ...(preset.sectionScopes.length > 0 ? { sectionScopes: preset.sectionScopes } : {}),
        ...(preset.subLists.length > 0 ? { subLists: preset.subLists } : {}),
        ...(preset.count ? { count: toSlideCount(preset.count) } : {}),
        ...(preset.parent
          ? {
              parent: {
                template: preset.parent.template as SlideTemplate,
                tagId: preset.parent.tagId,
                each: preset.parent.each,
              },
            }
          : {}),
        prompt: preset.prompt,
        randomizeChoicePositions: preset.randomizeChoicePositions,
        generateSentence: preset.generateSentence,
      },
    ];
  });

  const [create] = useMutation(CreateSectionPresetDocument);
  const [update] = useMutation(UpdateSectionPresetDocument);
  const [remove] = useMutation(DeleteSectionPresetDocument);

  return {
    sections,
    loading: loading || slidesLoading,
    saveSection: async (input: SectionPresetInput) => {
      const { data: created } = await create({ variables: { input } });
      await refetch();
      const saved = created?.createSectionPreset;
      return saved ? useFragment(SectionPresetFieldsFragmentDoc, saved).id : null;
    },
    updateSection: async (id: string, input: SectionPresetInput) => {
      await update({ variables: { id, input } });
      await refetch();
    },
    deleteSection: async (id: string) => {
      await remove({ variables: { id } });
      await refetch();
    },
  };
}
